import EventEmitter from 'eventemitter3';
import Element from '../UI/Element';
import { TextAlignment } from '../UI/TextAlignment';
import {
    getRewardData,
    IRewardData,
    RewardType,
} from '../../Utility/RewardHelper';
import { MathHelper } from '../../Utility/MathHelper';
import GameObject, { GameObjectEvent } from '../../GameObject/GameObject';
import Point from '../../Utility/Point';
import { MyPetMiniGameHUD } from './MyPetMiniGameHUD';
import Sprite from '../Sprite';
import Container from '../Container';
import Interactable from '../../Component/Interactable';
import Text from '../Text';
import TransformComponent from '../../Component/Transform';
import {
    Anim,
    StartAnimationClipAsync,
} from '../../System/Animation/AnimationHelper';
import Pet from '../../Prefabs/Pet/Pet';
import { PetAnimations } from '../../Utility/Helpers/Pets/PetAnimations';
import { PetId } from '../../Constants/Pets';
import { Easing } from '@tweenjs/tween.js';
import Armature from '../Armature';
import { DragonbonesDefault } from '../../System/Animation/Clip';
import { RenderSystemEvent } from '../../System/Render/RenderSystem';
import { PetArmature } from '../../Utility/Helpers/Pets/PetArmature';
import { sleep } from '../../Utility/async';
import Game from '../../Game';
import { AudioTrackType } from '../../System/Audio/AudioClip';
import { PlaySound } from '../../System/Audio/AudioHelper';
import { scaleSpriteToScreen } from 'Utility/LevelHelper';

enum UIEvent {
    DigItem = 'digItem',
    Claim = 'claim',
}

type Item = {
    container: GameObject<PIXI.Container>;
    view: GameObject<PIXI.Sprite>;
    highlight: GameObject<PIXI.Sprite>;
    reward: RewardItem | null;
};

type RewardItem = {
    container: GameObject<PIXI.Container>;
    view: GameObject<PIXI.Sprite>;
    godrays: Armature | null;
    jackpot: Armature | null;
};

export class MyPetMiniGameUI extends Element {
    private readonly events = new EventEmitter();
    private hud: MyPetMiniGameHUD;
    private items: Item[] = [];
    private hovers: Map<number, boolean>;
    private pet: Pet;
    private background: GameObject<PIXI.Sprite>;
    private wrapper: GameObject<PIXI.Sprite>;

    public create(params: {}) {
        this.hovers = new Map();
        super.create({ name: 'MyPetMiniGameUI' });

        this.wrapper = GameObject.Instantiate(Sprite, {
            anchor: new Point(0.5, 0.5),
            relativePosition: new Point(0.5, 0.5),
        });

        this.background = this.createBackground();
        scaleSpriteToScreen(this.background as Sprite);

        this.addChild(this.background);
        this.addChild(this.wrapper);

        this.pet = GameObject.Instantiate(Pet, {
            petId: PetId.Charly,
            x: 480,
            y: 120,
            pivot: new Point(170, 160),
            scale: new Point(-0.5, 0.5),
            armatureBlacklist: [PetArmature.MINI_GAME_FED, PetArmature.MINI_GAME_HUNGRY, PetArmature.MY_FARM],
        });
        this.wrapper.addChild(this.pet);

        this.items = Array.from({ length: 9 }).map((v, i) => {
            const item = this.createItem(i);

            item.view.on(GameObjectEvent.SELECTED, () => {
                this.hovers.set(i, false);
                this.updateHover();
                item.view.off(GameObjectEvent.POINTER_OVER);
                item.view.off(GameObjectEvent.POINTER_OUT);
                this.events.emit(UIEvent.DigItem, i);
            });

            item.view.on(GameObjectEvent.POINTER_OVER, () => {
                this.hovers.set(i, true);
                this.updateHover();
            });

            item.view.on(GameObjectEvent.POINTER_OUT, () => {
                this.hovers.set(i, false);
                this.updateHover();
            });

            this.wrapper.addChild(item.container);

            return item;
        });

        this.pet.initialized.then(() => {
            this.pet.play(PetAnimations.IDLE);
            this.pet.armatures.get(PetArmature.WALKING).timeScale = 2.4;
        });

        this.hud = GameObject.Instantiate(MyPetMiniGameHUD, {});
        this.addChild(this.hud);

        this.hud.addClaimClickListener(() => {
            this.events.emit(UIEvent.Claim);
        });
    }

    // Background

    private createBackground() {
        return GameObject.Instantiate(Sprite, {
            name: 'backgroundSprite',
            texture: 'MyPetMiniGame_BG',
            anchor: new Point(0.5, 0.5),
            relativePosition: new Point(0.5, 0.5),
        });
    }

    // Game layer

    private createItem(index: number): Item {
        const minXByRow = [-280, -320, -360];
        const row = Math.floor(index / 3);
        const minX = minXByRow[row];
        const position = {
            x: MathHelper.lerp(minX, 260, (index % 3) / 2),
            y: row * 140,
        };

        const container = GameObject.Instantiate(Container, {
            name: 'crop_container',
            position,
            scale: new Point(0.85, 0.85),
        });
        container.addComponent(TransformComponent);

        const highlight = GameObject.Instantiate(Sprite, {
            texture: 'pet_mini_highlight',
            anchor: new Point(0.5, 0.5),
            scale: new Point(1 + 0.2 * row, 1),
            alpha: 0.4,
            blendMode: PIXI.BLEND_MODES.ADD,
        });
        container.addChild(highlight);

        const view = GameObject.Instantiate(Sprite, {
            name: `item_${index}`,
            texture: `pet_mini_item${index}`,
            anchor: new Point(0.5, 0.66),
        });
        view.addComponent(Interactable, { enabled: true, buttonMode: true });
        container.addChild(view);

        return {
            container,
            view,
            highlight,
            reward: null,
        };
    }

    public async presentItems(): Promise<void> {
        const promises = this.items.map(async (item, i) => {
            await Anim(item.view, {
                scaleX: 1.2,
                scaleY: 1.2,
                duration: 300,
                delay: i * 200,
                yoyo: true,
            });
        });

        await Promise.all(promises);

        this.items.map((item) => {
            Anim(item.highlight, {
                alpha: 0,
                duration: 300,
            });
        });
    }

    private createRewardUI(reward: IRewardData, selected: boolean): RewardItem {
        const rewardContainer = GameObject.Instantiate(Container, {});
        const showJackpotAnimation = reward.isJackpot && selected;
        const showJackpotRegular = reward.isJackpot && !selected;
        const showGodrays = !showJackpotAnimation && selected;
        let godrays: Armature | null = null;
        let jackpot: Armature | null = null;

        if (showJackpotRegular) {
            const jackpotFx = GameObject.Instantiate(Sprite, {
                anchor: new Point(0.5, 0.5),
                texture: 'pet_minigame_jackpot_effect',
                scale: new Point(0.4, 0.4),
                y: -50,
            });
            rewardContainer.addChild(jackpotFx);
        }

        if (showGodrays) {
            godrays = GameObject.Instantiate(Armature, {
                dragonbonesName: 'digging_reward_rays',
                armatureName: 'Armature',
            });
            rewardContainer.addChild(godrays);

            const clip = new DragonbonesDefault(godrays, {
                animationName: 'animation',
            });
            StartAnimationClipAsync(clip);
        }

        if (showJackpotAnimation) {
            jackpot = GameObject.Instantiate(Armature, {
                dragonbonesName: 'digging_jackpot',
                armatureName: 'Armature',
                y: -30,
                scale: new Point(0.7, 0.7),
                playOnStart: false,
            });
            rewardContainer.addChild(jackpot);
        }

        if (showJackpotRegular) {
            const jackpotTitle = GameObject.Instantiate(Sprite, {
                name: 'jackpotTitle',
                anchor: new Point(0.5, 0.5),
                texture: 'pet_jackpot_tag',
                scale: new Point(0.6, 0.6),
                y: -90,
            });
            rewardContainer.addChild(jackpotTitle);
        }

        const rewardSpriteData = getRewardData(
            // Assuming reward.type matches what getRewardData supports
            reward.type as Parameters<typeof getRewardData>[0]
        );
        const rewardSprite = GameObject.Instantiate(Sprite, {
            name: 'reward',
            texture: reward.isJackpot
                ? 'pet_minigame_jackpot_coin'
                : rewardSpriteData.texture,
            anchor: new Point(0.5, 1),
            visible: !showJackpotAnimation,
        });

        const scaleFactor: { [key in RewardType]?: number } = {
            [RewardType.JOKER]: 1.2,
            [RewardType.COINS]: 0.92,
        };

        const scale =
            (70 / rewardSprite.container.height) *
            (scaleFactor[reward.type] ?? 1) *
            (reward.isJackpot ? 1.4 : 1);
        rewardSprite.container.scale.set(scale);
        rewardContainer.addChild(rewardSprite);

        if (godrays) {
            godrays.getComponent(TransformComponent).y =
                -rewardSprite.getComponent(TransformComponent).height / 2;
        }

        const rewardText = GameObject.Instantiate(Text, {
            name: 'text',
            text: `x${reward.value}`,
            style: {
                fontFamily: 'FrancoisOne',
                fontSize: 36,
                fill: '#fcffd9',
                align: TextAlignment.CENTER,
                strokeThickness: 4,
                stroke: '#811500',
            },
            anchor: new Point(0.5, 0),
            y: -14,
        });

        rewardContainer.addChild(rewardText);

        return {
            container: rewardContainer,
            view: rewardSprite,
            godrays,
            jackpot,
        };
    }

    private readonly updateHover = () => {
        this.items.forEach((item, turnipIndex) => {
            const scale = this.hovers.get(turnipIndex) ? 1.1 : 1;

            Anim(item.view, {
                scaleX: scale,
                scaleY: scale,
                duration: 100,
            });
        });
    };

    // Public part

    public onDiggingClick(listener: (itemIndex: number) => void) {
        this.events.on(UIEvent.DigItem, (itemIndex: number) => {
            listener(itemIndex);
        });
    }

    public onClaimClick(listener: () => void) {
        this.events.on(UIEvent.Claim, listener);
    }

    public setItemsInput(value: boolean): void {
        this.hovers.clear();
        this.updateHover();

        this.items.forEach(item => {
            const interactable = item.view.getComponent(Interactable);
            interactable.enabled = value;
            interactable.buttonMode = value;
        });
    }

    public addRewards(rewards: IRewardData[], selectedIndex: number): void {
        this.items.forEach((item, i) => {
            const rewardUi = this.createRewardUI(
                rewards[i],
                i === selectedIndex
            );
            rewardUi.container
                .getComponent(TransformComponent)
                .position.copy(
                    item.container.getComponent(TransformComponent).position
                );
            this.wrapper.addChild(rewardUi.container);
            item.reward = rewardUi;
            rewardUi.container.getComponent(TransformComponent).scale.set(0);
        });

        this.wrapper.addChild(this.items[selectedIndex].reward.container);
        this.wrapper.addChild(this.pet);
    }

    public async movePetToItem(index: number): Promise<void> {
        const item = this.items[index];
        const target = item.container
            .getComponent(TransformComponent)
            .position.clone();
        target.x -= 20;
        target.y -= 35;

        const petPosition = this.pet.getComponent(TransformComponent);
        const distance = Math.hypot(
            petPosition.x - target.x,
            petPosition.y - target.y
        );

        await this.pet.playOnce(PetAnimations.IDLE_TO_WALKING);
        this.pet.play(PetAnimations.WALKING);

        await Anim(this.pet, {
            x: target.x,
            y: target.y,
            duration: distance * 4,
            onUpdate: () => {
                this.scene.emit(
                    RenderSystemEvent.SORT_CHILDREN,
                    this.wrapper,
                );
            },
        });
    }

    public async animateDiggingItem(index: number): Promise<void> {
        const item = this.items[index];

        Anim(item.container, {
            alpha: 0,
            duration: 500,
            delay: 1000,
        });

        PlaySound('pet-digging');

        this.pet.playOnce(PetAnimations.DIGGING);
        await sleep(2400);
    }

    public async revealSelectedReward(index: number): Promise<void> {
        const item = this.items[index];
        const itemTransform = item.reward.container.getComponent(
            TransformComponent
        );
        itemTransform.x -= 60;
        itemTransform.y -= 70;

        if (item.reward.godrays) {
            item.reward.godrays.getComponent(TransformComponent).scale.set(0);

            Anim(item.reward.godrays, {
                scaleX: 1,
                scaleY: 1,
                duration: 500,
                delay: 300,
            });
        }

        if (item.reward.jackpot) {
            PlaySound('reward-high');
            const clip = new DragonbonesDefault(item.reward.jackpot, {
                animationName: 'animtion0',
                playTimes: 1,
            });
            StartAnimationClipAsync(clip);
        } else {
            PlaySound('reward-low');
        }

        await Anim(item.reward.container, {
            scaleX: 1.4,
            scaleY: 1.4,
            duration: 1200,
            easing: Easing.Elastic.Out,
        });
    }

    public async revealRewards(selectedIndex: number): Promise<void> {
        const promises: Promise<unknown>[] = this.items
            .filter((v, i) => i !== selectedIndex)
            .map(async (item, index) => {
                await this.revealReward(item, index * 200);
            });

        await Promise.all(promises);
    }

    private async revealReward(item: Item, delay: number): Promise<void> {
        const p1 = Anim(item.reward.container, {
            alpha: 0.65,
            scaleX: 1,
            scaleY: 1,
            duration: 200,
            delay,
            easing: Easing.Sinusoidal.Out,
        });

        const p2 = Anim(item.view, {
            alpha: 0.5,
            duration: 200,
            delay,
        });

        await Promise.all([p1, p2]);
    }

    public async showClaimButton(): Promise<void> {
        await this.hud.showClaimButton();
    }

    public showShareUi(): void {
        this.hud.showShareUi();
    }

    public disableClaimButton(): void {
        this.hud.disableClaimButton();
    }
}
